import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { formatDateOnly, relationshipLabel } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ListSkeleton } from "@/components/app/patterns/list-skeleton";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useFacility } from "@/lib/facility/facility";
import { XCircle } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { PlanQueueTable } from "./-components/plan-queue-table";
import { PlanSignDialog } from "./-components/plan-sign-dialog";

export const Route = createFileRoute("/_authenticated/_care/care-plan-signature-queue/")({
  component: SignatureQueuePage,
});

/* Sentence case, "Significant change", as the rest of the app labels things. */
const planTypeLabel = (value: string | null) => {
  if (!value) return "Assessment/Service Plan";
  const label = relationshipLabel(value);
  return label.charAt(0) + label.slice(1).toLowerCase();
};

/** Plans that name you as a signer and are still waiting for your signature. */
function SignatureQueuePage() {
  const { facilityId } = useFacility();
  /** The plans this signature covers: one row, or everything ticked. */
  const [signing, setSigning] = useState<{ planIds: string[]; title: string } | null>(null);
  const [chosen, setChosen] = useState<Record<string, boolean>>({});
  /** A plan somebody else reopened, or removed you from, fails on its own while the rest of the
   * batch signs. The toast is gone by the time you have read the list, so the failures stay on the
   * page until the next attempt. */
  const [failures, setFailures] = useState<{ planId: string; label: string; error: string }[]>([]);
  const [signError, setSignError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const queue = useQuery(orpc.care.plans.queue.queryOptions({ input: { facilityId } }));

  const rows = queue.data ?? [];
  const selected = rows.filter((row) => chosen[row.planId]).map((row) => row.planId);
  const rowLabel = (row: (typeof rows)[number]) =>
    `${row.residentLastName}, ${row.residentFirstName}. ${planTypeLabel(row.planType)} (${formatDateOnly(row.assessmentDate)})`;

  const sign = useMutation(
    orpc.care.plans.signAll.mutationOptions({
      onSuccess: async (result) => {
        const labelById = new Map(rows.map((row) => [row.planId, rowLabel(row)]));
        setFailures(
          result.results
            .filter((row) => !row.success)
            .map((row) => ({
              planId: row.planId,
              label: labelById.get(row.planId) ?? row.planId,
              error: row.error ?? "Could not be signed",
            })),
        );
        setSigning(null);
        setSignError(null);
        setChosen({});
        if (result.succeeded)
          toast.success(
            `${result.succeeded} signed${result.failed ? `, ${result.failed} could not be` : ""}`,
          );
        else toast.error("Nothing was signed");
        await queryClient.invalidateQueries({ queryKey: orpc.care.plans.key() });
      },
      // A wrong password is not a reason to close the dialog and start again.
      onError: (error) => setSignError(error.message),
    }),
  );

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Care plans to co-sign"
        count={queue.isSuccess ? rows.length : undefined}
        meta={
          queue.isPending || queue.isError
            ? undefined
            : rows.length === 0
              ? "Nothing waiting on you"
              : "Plans that name you as a signer. Review one on the chart, or tick several and sign them together."
        }
        actions={
          rows.length ? (
            <Button
              disabled={!selected.length}
              onClick={() => {
                setSignError(null);
                setSigning({
                  planIds: selected,
                  title: `Sign ${selected.length} ${selected.length === 1 ? "plan" : "plans"}`,
                });
              }}
            >
              {selected.length
                ? `Sign ${selected.length} ${selected.length === 1 ? "plan" : "plans"}`
                : "Sign selected"}
            </Button>
          ) : null
        }
      />

      {failures.length ? (
        <Alert variant="destructive">
          <XCircle className="size-4" />
          <AlertDescription>
            <p className="font-medium">
              {failures.length === 1
                ? "1 care plan could not be signed. Fix the reason below and sign it again."
                : `${failures.length} care plans could not be signed. Fix the reasons below and sign them again.`}
            </p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              {failures.map((failure) => (
                <li key={failure.planId}>
                  {failure.label}: {failure.error}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}

      {/* The states keep their card on a phone, where the table's card steps aside. */}
      {queue.isPending || queue.isError || !rows.length ? (
        <Card className="gap-0 py-0">
          {queue.isPending ? (
            <ListSkeleton />
          ) : queue.isError ? (
            <ReadFailed
              className="p-4"
              message="Could not load the plans waiting on your signature."
              error={queue.error}
              onRetry={() => void queue.refetch()}
              retrying={queue.isFetching}
            />
          ) : (
            <EmptyState
              title="Nothing waiting on you"
              description="Assessment and service plans that name you as a signer appear here once they are sent for signatures."
              action={
                <Button variant="outline" asChild>
                  <Link to="/care-plans">View all care plans</Link>
                </Button>
              }
            />
          )}
        </Card>
      ) : (
        <PlanQueueTable
          rows={rows}
          chosen={chosen}
          onChosen={setChosen}
          planTypeLabel={planTypeLabel}
          onSign={(row) => {
            setSignError(null);
            setSigning({
              planIds: [row.planId],
              title: `Sign the plan for ${row.residentLastName}, ${row.residentFirstName}`,
            });
          }}
        />
      )}
      <PlanSignDialog
        open={signing !== null}
        title={signing?.title ?? ""}
        pending={sign.isPending}
        error={signError}
        onOpenChange={(open) => {
          if (open) return;
          setSigning(null);
          setSignError(null);
        }}
        onSign={(password) => {
          setSignError(null);
          if (signing) sign.mutate({ password, planIds: signing.planIds });
        }}
      />
    </div>
  );
}
